import { TrackRatingInput } from '../../pages/review/components/album/TrackRatingEditor';
import AlbumActionConfirm from '../../pages/review/components/album/AlbumActionConfirm';
import ContactDraftGuard from '../contact/ContactDraftGuard';
import { useTrackFeedbackRating } from './useTrackFeedbackRating';
import { LuEyeOff, LuCircleCheck, LuSparkles, LuRotateCcw, LuCheck } from 'react-icons/lu';
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { discoveryApi } from '../../api/discovery';
import { useUserStore } from '../../stores/userStore';
import { saveCurrentPathAndRedirectToLogin } from '../../utils/authUtils';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import './RecommendationFeedback.css';
export default function RecommendationFeedback({
  item,
  kind = 'albums',
  event,
  onExcluded,
  onFeedbackApplied,
  disabled = false,
  albumDetail = false,
  renderTrigger,
  extraActions,
}) {
  const navigate = useNavigate();
  const lock = useRef(false);
  const { locale } = useWebLocale();
  const en = locale === 'en';
  const trackSheet = kind === 'tracks';
  const userId = useUserStore((state) => state.user?.id);
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const [notice, setNotice] = useState('');
  const [discard, setDiscard] = useState(null);
  const rating = useTrackFeedbackRating(item, trackSheet && open, (deleted) => {
    setOpen(false);
    setNotice(
      en
        ? deleted
          ? 'Rating deleted.'
          : 'Rating saved.'
        : deleted
          ? '곡 평가를 삭제했어요.'
          : '곡 평가를 저장했어요.',
    );
  });
  const busy = pending || rating.busy || disabled;
  const guard = useRef({});
  guard.current = { dirty: trackSheet && rating.dirty, busy: open && busy, released: !open };
  const confirm = (action) => {
    if (busy) return;
    if (trackSheet && rating.dirty) setDiscard(() => action);
    else action();
  };
  const close = () => confirm(() => setOpen(false));
  const draftCopy = (key) =>
    ({
      'editor.busyTitle': en ? 'Saving…' : '저장 중이에요',
      'editor.busyBody': en
        ? 'Please wait for saving to finish.'
        : '저장이 끝날 때까지 기다려 주세요.',
      'common.confirm': en ? 'OK' : '확인',
      'editor.discardTitle': en ? 'Discard changes?' : '작성 내용 버리기',
      'editor.discardBody': en
        ? 'Your unsaved rating will be lost.'
        : '저장하지 않은 곡 평가가 사라집니다.',
      'editor.keepWriting': en ? 'Keep editing' : '계속 작성',
      'editor.discard': en ? 'Discard' : '버리기',
    })[key];
  const done = () => {
    if (busy) return;
    if (!rating.dirty) {
      setOpen(false);
      return;
    }
    if (!userId) {
      confirm(() => {
        setOpen(false);
        saveCurrentPathAndRedirectToLogin(navigate);
      });
      return;
    }
    void rating.save();
  };
  const feedbackKey = ['web-discovery-feedback', userId, kind, item.id];
  const previous = useQuery({
    queryKey: feedbackKey,
    queryFn: ({ signal }) => discoveryApi.feedback(kind, item.id, signal),
    enabled: Boolean(open && userId),
    staleTime: 0,
    retry: false,
  });
  useEffect(() => {
    setOpen(false);
    setPending(false);
    setError(false);
    setNotice('');
    setDiscard(null);
  }, [userId, item.id]);
  const submit = async (type) => {
    if (!userId && (albumDetail || trackSheet)) {
      setOpen(false);
      saveCurrentPathAndRedirectToLogin(navigate);
      return;
    }
    if (!userId || lock.current || useUserStore.getState().user?.id !== userId) return;
    lock.current = true;
    setPending(true);
    setError(false);
    try {
      if (type)
        await discoveryApi.recordEvents([
          {
            ...event,
            eventType: 'RECOMMENDATION_FEEDBACK',
            feedbackType: type,
            ...(kind === 'tracks'
              ? { trackId: item.id, albumId: item.albumId }
              : { albumId: item.id }),
          },
        ]);
      else await discoveryApi.clearFeedback(kind, item.id);
      if (useUserStore.getState().user?.id !== userId) return;
      client.setQueryData(feedbackKey, { feedbackType: type });
      if (trackSheet) onFeedbackApplied?.(item, type);
      if (['NOT_INTERESTED', 'ALREADY_KNOWN'].includes(type)) {
        client.setQueriesData({ queryKey: ['web-discovery', userId] }, (data) => {
          if (!data) return data;
          const keep = (album) =>
            album.id !== item.id &&
            (item.versionGroupId == null || album.versionGroupId !== item.versionGroupId);
          const excluded = new Set([
            item.id,
            ...(data.albums || []).filter((album) => !keep(album)).map((album) => album.id),
          ]);
          return kind === 'tracks'
            ? {
                ...data,
                tracks: (data.tracks || []).filter((track) => track.id !== item.id),
                excludedTrackIds: [...(data.excludedTrackIds || []), item.id],
              }
            : {
                ...data,
                albums: (data.albums || []).filter(keep),
                revisitAlbums: (data.revisitAlbums || []).filter(keep),
                tracks: (data.tracks || []).filter((track) => !excluded.has(track.albumId)),
                excludedAlbumIds: [...(data.excludedAlbumIds || []), ...excluded],
              };
        });
        onExcluded?.(item.id);
      }
      // Mark other surfaces stale without racing a just-confirmed exclusion.
      void client.invalidateQueries({ queryKey: ['web-discovery', userId], refetchType: 'none' });
      setOpen(false);
      if (trackSheet && onFeedbackApplied) {
        setNotice('');
        return;
      }
      setNotice(
        albumDetail
          ? {
              NOT_INTERESTED: en ? 'We’ll show less of this album.' : '관심 없음으로 반영했어요.',
              ALREADY_KNOWN: en
                ? 'Marked as an album you already know.'
                : '이미 아는 앨범으로 반영했어요.',
              MORE_LIKE_THIS: en
                ? 'We’ll show you more music like this.'
                : '비슷한 음악을 더 보여드릴게요.',
            }[type] || (en ? 'Recommendation choice cleared.' : '추천 조정을 취소했어요.')
          : en
            ? 'Preference saved.'
            : '추천에 반영했어요.',
      );
    } catch {
      if (useUserStore.getState().user?.id === userId) setError(true);
    } finally {
      lock.current = false;
      if (useUserStore.getState().user?.id === userId) setPending(false);
    }
  };
  const labels = trackSheet
    ? en
      ? ['Not my taste', 'I already know this track', 'More like this']
      : ['취향이 아니에요', '이미 아는 곡', '이런 음악 더 보기']
    : albumDetail
      ? en
        ? ['Not interested', 'I already know this album', 'More like this']
        : ['관심 없음', '이미 아는 앨범', '이런 음악 더 보기']
      : en
        ? ['Not interested', 'Already know it', 'More like this']
        : ['관심 없어요', '이미 알고 있어요', '비슷한 음악 더 보기'];
  return (
    <>
      {trackSheet && open && <ContactDraftGuard state={guard} t={draftCopy} />}
      {discard && (
        <AlbumActionConfirm
          kind="discard"
          en={en}
          onCancel={() => setDiscard(null)}
          onConfirm={() => {
            const action = discard;
            setDiscard(null);
            rating.select(undefined);
            guard.current.released = true;
            action();
          }}
        />
      )}
      {renderTrigger ? (
        renderTrigger(() => {
          if (disabled) return;
          setError(false);
          setOpen(true);
        })
      ) : (
        <button
          className="pp-feedback-button"
          disabled={disabled}
          aria-label={`${en ? 'Recommendation preferences' : '추천 취향 설정'} · ${item.title || item.albumTitle}`}
          onClick={() => {
            setError(false);
            setOpen(true);
          }}
        >
          ···
        </button>
      )}
      {notice && (
        <span role="status" className="pp-secondary">
          {notice}
        </span>
      )}
      <Dialog
        className={albumDetail ? 'pp-album-options' : trackSheet ? 'pp-track-options' : undefined}
        aria-labelledby={albumDetail ? 'album-options-title' : undefined}
        open={open}
        onClose={close}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id={albumDetail ? 'album-options-heading' : undefined}>
          <span id={albumDetail ? 'album-options-title' : undefined}>
            {albumDetail
              ? en
                ? 'Tune your recommendations'
                : '추천을 조정할게요'
              : trackSheet
                ? en
                  ? 'How was this track?'
                  : '이 곡은 어땠나요?'
                : item.title || item.albumTitle}
          </span>
          {albumDetail && (
            <Button
              className="pp-options-close"
              aria-label={en ? 'Close' : '닫기'}
              disabled={busy}
              onClick={close}
            >
              ×
            </Button>
          )}
          {trackSheet && (
            <span className="pp-track-rating-actions">
              {rating.value != null && (
                <Button disabled={busy} onClick={() => rating.select(null)}>
                  {en ? 'Delete' : '삭제'}
                </Button>
              )}
              <Button
                className="pp-track-rating-done"
                disabled={
                  busy || Boolean(userId && (rating.query.isPending || rating.query.isError))
                }
                onClick={done}
              >
                {en ? 'Done' : '완료'}
              </Button>
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          {trackSheet && (
            <div className="pp-track-options-summary">
              {item.albumImageUrl ? (
                <img src={item.albumImageUrl} alt="" />
              ) : (
                <span className="pp-track-options-artwork" aria-hidden>
                  P
                </span>
              )}
              <div>
                <strong>{item.title}</strong>
                <span>{item.artistName || item.albumTitle}</span>
                <small>
                  {en
                    ? 'Your rating improves future recommendations.'
                    : '평가가 다음 추천에 반영돼요.'}
                </small>
              </div>
            </div>
          )}

          {trackSheet && (
            <div className="pp-home-track-rating">
              {userId && rating.query.isPending ? (
                <p role="status">{en ? 'Loading…' : '불러오는 중…'}</p>
              ) : userId && rating.query.isError ? (
                <div role="alert">
                  <p>{en ? 'Could not load your rating.' : '곡 평가를 불러오지 못했어요.'}</p>
                  <Button onClick={() => rating.query.refetch()}>
                    {en ? 'Retry rating' : '평가 다시 불러오기'}
                  </Button>
                </div>
              ) : (
                <>
                  <TrackRatingInput
                    statusLabel
                    title={item.title}
                    en={en}
                    value={rating.value}
                    disabled={busy}
                    onChange={rating.select}
                  />
                </>
              )}
              {rating.error && (
                <p role="alert">
                  {en
                    ? 'Could not save. Your rating is still here.'
                    : '저장하지 못했어요. 선택한 평가는 유지됩니다.'}
                </p>
              )}
            </div>
          )}
          {!userId && !albumDetail && !trackSheet ? (
            <Button
              component={Link}
              to={`/login?redirectUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            >
              {en ? 'Sign in to personalize recommendations' : '로그인하고 추천 취향 설정하기'}
            </Button>
          ) : (
            <>
              {userId && previous.isPending ? (
                <p role="status">{en ? 'Loading…' : '불러오는 중…'}</p>
              ) : userId && previous.isError ? (
                <Button onClick={() => previous.refetch()}>{en ? 'Retry' : '다시 시도'}</Button>
              ) : (
                <div className="pp-feedback-options">
                  {['NOT_INTERESTED', 'ALREADY_KNOWN', 'MORE_LIKE_THIS'].map((type, index) => (
                    <Button
                      key={type}
                      disabled={busy}
                      aria-pressed={previous.data?.feedbackType === type}
                      data-feedback={type}
                      onClick={() => confirm(() => submit(type))}
                    >
                      {(albumDetail || trackSheet) &&
                        React.createElement([LuEyeOff, LuCircleCheck, LuSparkles][index], {
                          size: 20,
                          'aria-hidden': true,
                        })}
                      <span>{labels[index]}</span>
                      {previous.data?.feedbackType === type ? (
                        albumDetail || trackSheet ? (
                          <LuCheck size={19} aria-hidden />
                        ) : (
                          ' ✓'
                        )
                      ) : (
                        ''
                      )}
                    </Button>
                  ))}
                  {previous.data?.feedbackType && (
                    <Button disabled={busy} onClick={() => confirm(() => submit(null))}>
                      {(albumDetail || trackSheet) && <LuRotateCcw size={20} aria-hidden />}
                      {albumDetail || trackSheet
                        ? en
                          ? 'Clear recommendation choice'
                          : '추천 조정 취소'
                        : en
                          ? 'Clear preference'
                          : '취향 설정 취소'}
                    </Button>
                  )}
                </div>
              )}
              {error && (
                <p role="alert">
                  {en
                    ? 'Couldn’t save. Please try again.'
                    : '저장하지 못했어요. 다시 시도해 주세요.'}
                </p>
              )}
            </>
          )}
          {extraActions && <div className="pp-feedback-options">{extraActions(close, busy)}</div>}
        </DialogContent>
        {!trackSheet && !albumDetail && (
          <DialogActions>
            <Button disabled={busy} onClick={close}>
              {en ? 'Close' : '닫기'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
